import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService, SortEvent } from 'primeng/api';
import { BehaviorSubject, forkJoin, Observable, Subject, take, takeUntil } from 'rxjs';
import { LocationService } from 'src/app/services/location.service';
import { PeopleService } from 'src/app/services/people.service';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { AppConstants } from '../../../shared/app.constants';

@Component({
  selector: 'ness-merge-people',
  templateUrl: './merge-people.component.html'
})
export class MergePeopleComponent implements OnChanges {

  @Input() selectedPeopleToMerge: ServerModels.ENESS_PersonDTO[] = [];
  mergePeopleColumns = [
    { field: 'ENESS_XREF_PeopleFBIFileNumbers', header: 'FBI #' },
    { field: 'ENESS_XREF_PeopleStateIDNumbers', header: 'State ID' },
    { field: 'LastName', header: 'Last Name' },
    { field: 'FirstName', header: 'First Name' },
    { field: 'DateOfBirth', header: 'Birth Date' },
    { field: 'SSN', header: 'SSN' }
  ];
  defaultTableRows: number = AppConstants.defaultTableRows;
  disableMergeButton: boolean = true;
  showPersonDetails: boolean = false;
  races: ServerModels.ENESS_REF_Races[];
  genders: ServerModels.ENESS_REF_Genders[];
  displaySidebar: boolean = false;
  hasPermissionToEditAddress: boolean = false;
  personAddressType: string;
  personAddressTypeFromRef: string;
  personAddressTypeOther: string;
  showPersonAddressDetails: boolean = false;
  showPersonAddressDetailForm: boolean = false;
  showLocationView: boolean = false;
  showEditLocationView: boolean = false;

  @Output() personRemoved: EventEmitter<string> = new EventEmitter<string>();

  private _personDTO: ServerModels.ENESS_PersonDTO = null;
  personDTO: BehaviorSubject<ServerModels.ENESS_PersonDTO> = new BehaviorSubject(this._personDTO);
  personDTO$: Observable<ServerModels.ENESS_PersonDTO> = this.personDTO.asObservable();

  personAddress: ServerModels.ENESS_AddressDTO;

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private peopleService: PeopleService,
    private locationService: LocationService,
    private messageService: MessageService,
    private router: Router) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedPeopleToMerge']) {
      if (this.selectedPeopleToMerge.length == 2 || this.selectedPeopleToMerge.length == 3) {
        this.disableMergeButton = false;
      }
    }
  }

  public viewPersonDetails(personId: string): void {
    this.displaySidebar = true;
    this.showPersonAddressDetails = false;
    this.showPersonAddressDetailForm = false;
    this.personAddress = null;
    this.peopleService.getPersonDetailsDTOById(personId).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (person: ServerModels.ENESS_PersonDTO | null) => {
        this._getPersonLECases(personId);
        this._getPersonInfoReports(personId);
        this._getPersonResults(personId);
        this._personDTO = person;
        this.personDTO.next(this._personDTO);
      },
      error: (err: any) => {
        this.messageService.addAll([{ key: 'viewPersonDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      },
      complete: () => { this.showPersonDetails = true; }
    });
  }

  public viewAddressDetails(addressDetails: any[]): void {
    this.displaySidebar = true;
    this.showPersonDetails = false;
    this.showPersonAddressDetails = true;
    this.showPersonAddressDetailForm = false;
    this.hasPermissionToEditAddress = addressDetails[1];
    this.personAddressTypeFromRef = addressDetails[2];
    this.personAddressTypeOther = addressDetails[3];
    this.personAddressType = addressDetails[2] === "Other" ? addressDetails[2] + ": " + addressDetails[3] : addressDetails[2];
    forkJoin({
      address: this.locationService.getAddressDetailsById(addressDetails[0]),
      leCases: this.locationService.getAddressLECases(addressDetails[0]),
      people: this.locationService.getAddressPeople(addressDetails[0])
    }).pipe(take(1)).subscribe({
      next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this.showLocationView = true;
        this.personAddress = val.address;
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.locationService.addressPeopleSubj.next(val.people);
      },
      error: (err: any) => {
        this.messageService.addAll([{ key: 'viewAddressDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      }
    })
  }

  public editLocation(editLocation: string): void {
    if (editLocation === 'Edit') {
      this.showPersonDetails = false;
      this.showPersonAddressDetails = false;
      this.showPersonAddressDetailForm = true;
      this.showLocationView = false;
      this.showEditLocationView = true;
    }
  }

  public onLocationFormClose(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.viewAddressDetails([newAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
  }

  public onViewPersonDetailsClose(): void {
    this._personDTO = null;
    this.personDTO.next(this._personDTO);
    this.peopleService.personLECasesSubj.next(null);
    this.peopleService.personInfoReportsSubj.next(null);
    this.peopleService.personResultsSubj.next(null);
    this.showPersonDetails = false;
    this.displaySidebar = false;
  }

  public onClickCloseButton(): void {
    if (this.showPersonDetails) {
      this.onViewPersonDetailsClose();
    }
    if (this.showPersonAddressDetails) {
      this.showPersonAddressDetails = false;
      this.showPersonDetails = true;
    }
    if (this.showPersonAddressDetailForm) {
      this.viewAddressDetails([this.personAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
    }
  }

  public closeForm(open: boolean): void {
    if (!open) {
      this.showPersonDetails = false;
    }
  }

  public mergePeople(): void {
    if (this.save()) {
      this.router.navigate(['/manageduplicates/compare-and-merge-people']);
    }
  }

  private save(): boolean {
    if (this.selectedPeopleToMerge.length == 0) {
      return false;
    }

    const people = [] as string[];
    this.selectedPeopleToMerge.forEach(person => people.push(person.PersonID));

    const mergePeople: string = people.join(';');

    sessionStorage.setItem('mergePeople', mergePeople);

    return true;
  }

  public removeFromMergeList(personId: string): void {
    this.selectedPeopleToMerge = this.selectedPeopleToMerge.filter(x => x.PersonID != personId);
    if (this.selectedPeopleToMerge.length < 2 || this.selectedPeopleToMerge.length > 3) {
      this.disableMergeButton = true;
    }
    else {
      this.disableMergeButton = false;
    }
    this.personRemoved.emit(personId);
  }

  private _getPersonResults(personId: string): void {
    if (AppPermissions.canViewResults) {
      this.peopleService.getPersonResults(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personResultsSubj.next(result),
        (err: any) => {
          this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        });
    }
  }

  private _getPersonInfoReports(personId: string): void {
    if (AppPermissions.canViewIRs) {
      this.peopleService.getPersonInfoReports(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personInfoReportsSubj.next(result),
        (err:any) => {
          this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        });
    }
  }

  private _getPersonLECases(personId: string): void {
    this.peopleService.getPersonLECases(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: ServerModels.PersonLECases[]) =>
      this.peopleService.personLECasesSubj.next(result),
      (err:any) => {
        this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      });
  }

  public customSort(event: SortEvent) {
    event.data.sort((data1, data2) => {
      let value1 = event.field == 'ENESS_XREF_PeopleStateIDNumbers' ? `${data1[event.field][0]['StateAbbreviation']}${data1[event.field][0]['StateIDNumbers']}` : data1[event.field];
      let value2 = event.field == 'ENESS_XREF_PeopleStateIDNumbers' ? `${data2[event.field][0]['StateAbbreviation']}${data2[event.field][0]['StateIDNumbers']}` : data2[event.field];
      let result = null;

      if (value1 == null && value2 != null)
        result = -1;
      else if (value1 != null && value2 == null)
        result = 1;
      else if (value1 == null && value2 == null)
        result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string')
        result = value1.localeCompare(value2);
      else
        result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

      return (event.order * result);
    });
  }

}
